<template>
  <SoDataTable
    ref="dataTable"
    v-model:filters="filters"
    :headers="headers"
    :items="items"
    :loading="elecData.isFetching"
    :sort-by="[{ key: 'id', order: 'desc' }]"
    filter-width="440px"
  >
    <template #tableActions>
      <div class="d-flex gap-1">
        <SoButton
          v-if="isAddTradeAllowed"
          title="Add Trade"
          icon="plus"
          type="primary"
          large
          data-testid="elec-trade-table-add-btn"
          @click="isAddTradeDialogOpen = true"
        />
        <SoButton
          title="Export"
          icon="download"
          type="primary"
          large
          data-testid="elec-trade-table-export-btn"
          @click="exportElecTrades()"
        />
      </div>
    </template>
    <template #itemActions="rowData">
      <div class="d-flex gap-1 justify-end">
        <SoButton
          v-if="userHasMOPermissions"
          title="confirm"
          type="table-action"
          icon="pencil sm"
          :data-testid="`elec-trade-table-confirm-btn-${rowData.item.id}`"
          @click="handleConfirmClick(rowData.item)"
        />
        <SoButton
          v-if="userHasMOPermissions || rowData.item.canEdit"
          title="edit"
          type="table-action"
          icon="pencil sm"
          :data-testid="`elec-trade-table-update-btn-${rowData.item.id}`"
          @click="handleEditClick(rowData.item)"
        />
        <SoButton
          v-if="userHasMOPermissions && rowData.item.locked"
          title="unlock"
          type="table-action"
          icon="lock-open sm"
          :data-testid="`elec-trade-table-unlock-btn-${rowData.item.id}`"
          @click="handleUnlockClick(rowData.item)"
        />
      </div>
    </template>
    <template #expandableData="rowData">
      <tr v-for="historyItem in rowData.history" :key="historyItem.id">
        <td v-for="column in historyColumns" :key="column">
          {{ parseHistoryData(column, historyItem) }}
        </td>
        <td />
      </tr>
    </template>
    <template #item.id="{ internalItem }">
      <v-badge
        v-if="internalItem.raw.history.length !== 0"
        color="red"
        dot
        style="width: 70%"
      >
        {{ internalItem.raw.id }}
      </v-badge>
    </template>
  </SoDataTable>
  <SoDialog
    v-model="isAddTradeDialogOpen"
    :card-title="
      isEditForm ? 'Edit Electricity Trade' : 'Add Electricity Trade'
    "
    data-testid="elec-trade-table-add-dialog"
    @on-close="resetAddTradeModal()"
  >
    <SoForm
      ref="add-elec-trade-form"
      v-model="isAddTradeValid"
      @submit="addElecTrade()"
    >
      <SoSelect
        v-model="tradeProductField"
        :items="Object.keys(tradeProducts)"
        :rules="[rules.required]"
        label="Product"
        @update:model-value="onUpdateProductField($event)"
      />
      <div class="two-part-dropdown">
        <SoSelect
          v-model="periodFirstField"
          :items="tradePeriodFirstFieldOptionsElec"
          :rules="[rules.required]"
          label="Period"
          type="combobox"
          clearable
        />
        <SoSelect
          v-model="periodSecondField"
          class="no-label-spacer"
          :items="tradePeriodSecondFieldOptions"
          :rules="periodFieldsValidation"
          clearable
        />
      </div>
      <SoTextField
        v-model="priceField"
        :rules="[rules.required]"
        type="number"
        label="Price (£/MWh)"
      />
      <div class="two-part-dropdown">
        <SoSelect
          v-model="buySellField"
          :items="Object.keys(TradeBuySellValues)"
          :rules="[rules.required]"
          label="Volume (MW)"
        />
        <SoTextField
          v-model="volumeField"
          class="no-label-spacer"
          :rules="[rules.required, rules.positiveNumber]"
          type="number"
          :min="0"
        />
      </div>
      <SoTextField
        v-model="percentOfBaseloadField"
        :rules="[rules.positiveNumber]"
        type="number"
        :min="0"
        label="% of Baseload"
        :disabled="!allowsPercentOfBaseload"
      />
      <SoDatePicker
        v-model="invoiceDateRange"
        label="Invoice Date Range"
        type="daterange"
        :error="invoiceDateRangeError"
      />
      <div class="two-part-dropdown">
        <SoSelect
          v-model="counterPartyFirstField"
          :items="tradeCounterpartyFirstFieldOptions"
          :rules="[rules.required]"
          label="CounterParty"
        />
        <SoSelect
          v-model="counterPartySecondField"
          class="no-label-spacer"
          :items="tradeCounterpartySecondFieldOptions"
          :rules="[rules.required]"
        />
      </div>
      <SoTextField
        v-model="traderField"
        :rules="[rules.required]"
        label="Trader"
        readonly
      />
      <SoTextarea v-model="notesField" label="Notes" />
      <v-slide-x-transition>
        <SoAlert
          v-if="elecTradeMutator.isError"
          type="error"
          class="my-4"
          data-testid="elec-trade-add-trade-error"
        >
          {{ elecTradeMutator.error }}
        </SoAlert>
        <SoAlert
          v-else-if="elecTradeMutator.isSuccess"
          type="success"
          class="my-4"
        >
          Trade added.
        </SoAlert>
        <SoAlert
          v-if="updateElecTradeMutator.isError"
          type="error"
          class="my-4"
          data-testid="elec-trade-update-trade-error"
        >
          {{ updateElecTradeMutator.error }}
        </SoAlert>
        <SoAlert
          v-else-if="updateElecTradeMutator.isSuccess"
          type="success"
          class="my-4"
        >
          Trade updated.
        </SoAlert>
      </v-slide-x-transition>
      <div class="d-flex justify-end pt-4 gap-2">
        <SoButton
          title="Cancel"
          large
          data-testid="elec-trade-table-cancel-trade-btn"
          @click="isAddTradeDialogOpen = false"
        />
        <SoButton
          title="Clear"
          large
          data-testid="elec-trade-table-clear-trade-btn"
          @click="clearTradeForm()"
        />
        <SoButton
          title="Save"
          type="primary"
          :disabled="!isAddTradeValid"
          large
          submit
          data-testid="elec-trade-table-save-trade-btn"
        />
      </div>
    </SoForm>
  </SoDialog>
  <ConfirmTradeModal
    v-model="isConfirmTradeDialogOpen"
    data-testid="confirm-elec-trade-modal"
    @submit="($event) => confirmElecTrade($event)"
  >
    <template #alerts>
      <SoAlert
        v-if="confirmElecTradeMutator.isError"
        type="error"
        class="my-4"
        data-testid="elec-trade-confirm-trade-error"
      >
        {{ confirmElecTradeMutator.error }}
      </SoAlert>
      <SoAlert
        v-else-if="confirmElecTradeMutator.isSuccess"
        type="success"
        class="my-4"
      >
        Trade Signed.
      </SoAlert>
    </template>
  </ConfirmTradeModal>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { TradingDataService } from "@/services/operations/TradingDataService"
  import {
    elecTradeTableFilters,
    TradeBuySell,
    TradeBuySellValues,
    tradeCounterpartyFirstFieldOptions,
    tradeCounterpartySecondFieldOptions,
    tradePeriodFirstFieldOptionsElec,
    tradePeriodSecondFieldOptions,
    tradeProductOptions,
    tradeProducts,
  } from "@/constants/tradeOptions"
  import {
    FilterCheckbox,
    FilterType,
    SoDataTableColumnType,
  } from "@/types/components/SoDataTable"
  import { TradesMutator } from "@/mutators/trades/TradesMutator"
  import {
    ITradesConfirmElecData,
    ITradesElecData,
  } from "@/models/operations/tradingData/elecData.types"
  import ConfirmTradeModal from "./ConfirmTradeModal.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import rules from "@/validators/rules"
  import dayjs, { Dayjs } from "dayjs"
  import { AuthService } from "@/services/AuthService"
  import {
    getDateRangeForPeriod,
    getHistoryForElecTrade,
    parseHistoryData,
  } from "@/helpers/tradeHelper"
  import { ROLES } from "@/services/PermissionsService"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import * as xlsx from "xlsx-republish"

  export default defineComponent({
    components: {
      SoTextarea,
      ConfirmTradeModal,
      SoDataTable,
      SoButton,
      SoDialog,
      SoForm,
      SoSelect,
      SoTextField,
      SoDatePicker,
      SoAlert,
    },
    data() {
      return {
        elecData: TradingDataService.getElecData(),
        elecHistoryData: TradingDataService.getElecHistoryData(),
        elecTradeMutator: TradesMutator.insertElecTrade(),
        elecTradeHistoryMutator: TradesMutator.insertElecTradeHistory(),
        confirmElecTradeMutator: TradesMutator.confirmElecTrade(),
        updateElecTradeMutator: TradesMutator.updateElecTrade(),
        unlockElecTradeMutator: TradesMutator.unlockElecTrade(),
        tradeProducts,
        rules,
        tradePeriodFirstFieldOptionsElec,
        tradePeriodSecondFieldOptions,
        tradeCounterpartyFirstFieldOptions,
        tradeCounterpartySecondFieldOptions,
        TradeBuySellValues,

        isAddTradeDialogOpen: false,
        isAddTradeValid: false,
        isEditForm: false,
        tradeProductField: null as tradeProductOptions | null,
        periodFirstField: null as string | null,
        periodSecondField: null as string | null,
        priceField: null as number | null,
        buySellField: null as TradeBuySell | null,
        volumeField: null as number | null,
        counterPartyFirstField: null as string | null,
        counterPartySecondField: null as string | null,
        traderField: AuthService.getCurrentUser().name,
        percentOfBaseloadField: null as number | null,
        invoiceDateRange: null as Date[] | null,
        invoiceDateRangeError: "",
        notesField: null as string | null,

        isConfirmTradeDialogOpen: false,
        selectedRowId: null as number | null,
        filters: elecTradeTableFilters,
      }
    },
    computed: {
      headers() {
        return [
          {
            title: "ID",
            key: "id",
            sortable: true,
          },
          {
            title: "Date",
            key: "date",
            sortable: true,
            fallbackValue: "-",
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Product",
            key: "product",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Period",
            key: "period",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Price (£/MWh)",
            key: "price",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Volume (MW)",
            key: "volume",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Total Volume (MWh)",
            key: "totalVolume",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "% of Baseload",
            key: "percentOfBaseload",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Invoice Date From",
            key: "invoiceDateFrom",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Invoice Date To",
            key: "invoiceDateTo",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Counterparty",
            key: "counterParty",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Trader",
            key: "trader",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Confirm Received Date",
            key: "confirmReceivedDate",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Confirm Signed Date",
            key: "confirmSignedDate",
            fallbackValue: "-",
            sortable: true,
            type: SoDataTableColumnType.DATE,
          },
          {
            title: "Confirm Signed By",
            key: "confirmSignedBy",
            fallbackValue: "-",
            sortable: true,
          },
          {
            title: "Notes",
            key: "notes",
            fallbackValue: "-",
            sortable: true,
          },
        ]
      },
      historyColumns() {
        return this.headers.map((header) => header.key)
      },
      items() {
        const items = this.elecData.data ?? []
        return items.map((trade) => {
          return {
            ...trade,
            canEdit:
              trade.trader == AuthService.getCurrentUser().name &&
              !trade.locked,
            history: getHistoryForElecTrade(trade, this.sortedHistoryItems),
          }
        })
      },
      sortedItems() {
        return [...this.elecData.data].sort((a, b) => (a.id > b.id ? -1 : 1))
      },
      sortedHistoryItems() {
        return [...this.elecHistoryData.data].sort((a, b) =>
          a.id > b.id ? -1 : 1,
        )
      },
      isPriceFieldEmpty() {
        return this.priceField === null
      },
      periodFields() {
        return [this.periodFirstField, this.periodSecondField]
      },
      periodFieldsValidation() {
        return this.periodFirstField &&
          !tradePeriodFirstFieldOptionsElec.includes(this.periodFirstField)
          ? []
          : [rules.required]
      },
      isAddTradeAllowed() {
        return (
          AuthService.isDeveloper() ||
          AuthService.getUserRoles().includes(ROLES.TRADING_FO)
        )
      },
      userHasMOPermissions() {
        return (
          AuthService.isDeveloper() ||
          AuthService.getUserRoles().includes(ROLES.TRADING_MO)
        )
      },
      allowsPercentOfBaseload() {
        return (
          !this.tradeProductField ||
          tradeProducts[this.tradeProductField].allowsPercentOfBaseload
        )
      },
      isPercentOfBaseloadFieldEmpty() {
        return this.percentOfBaseloadField === null
      },
    },
    watch: {
      items(val) {
        val.forEach((trade: ITradesElecData) => {
          const computedFilters = [
            "trader",
            "confirmSignedBy",
            "period",
            "counterParty",
          ]

          computedFilters.forEach((column) => {
            if (
              this.filters[column].type === FilterType.CHECKBOX &&
              trade[column] !== null
            ) {
              ;(this.filters[column] as FilterCheckbox).options[trade[column]] =
                trade[column]
            }
          })
        })
      },
      isAddTradeDialogOpen(val) {
        if (!val) {
          if (
            this.elecTradeMutator.isSuccess ||
            this.elecTradeMutator.isError
          ) {
            this.elecTradeMutator.reset()
          } else if (
            this.updateElecTradeMutator.isSuccess ||
            this.updateElecTradeMutator.isError
          ) {
            this.updateElecTradeMutator.reset()
          }
        }
      },
      isConfirmTradeDialogOpen(val) {
        if (!val) {
          if (
            this.confirmElecTradeMutator.isSuccess ||
            this.confirmElecTradeMutator.isError
          ) {
            this.confirmElecTradeMutator.reset()
          }
        }
      },
      periodFields(val) {
        const [period, year] = val

        if (period && year) {
          this.invoiceDateRange = getDateRangeForPeriod(period, year)
        } else this.invoiceDateRange = null
      },
    },
    methods: {
      parseHistoryData,
      async addElecTrade() {
        if (!this.invoiceDateRange) {
          this.invoiceDateRangeError = "Date is required"
          return
        }
        this.invoiceDateRangeError = ""

        const tradeData = {
          id: this.isEditForm
            ? Number(this.selectedRowId)
            : Number(this.sortedItems[0].id) + 1,
          date: null,
          product: this.tradeProductField,
          period: this.getPeriod(),
          price: this.isPriceFieldEmpty ? null : Number(this.priceField),
          volume:
            TradeBuySellValues[this.buySellField || "Buy"].multiplier *
            Number(this.volumeField),
          invoiceDateFrom: dayjs(this.invoiceDateRange[0]),
          invoiceDateTo: dayjs(this.invoiceDateRange[1]),
          counterParty: `${this.counterPartyFirstField}-${this.counterPartySecondField}`,
          trader: this.traderField,
          notes: this.notesField,
          percentOfBaseload: this.isPercentOfBaseloadFieldEmpty
            ? null
            : Number(this.percentOfBaseloadField),
        }

        if (this.isEditForm) {
          const tradeToBeEdited = this.elecData.data.find(
            (trade) => trade.id === this.selectedRowId,
          )

          if (tradeToBeEdited) {
            await this.elecTradeHistoryMutator.mutateAsync({
              ...tradeToBeEdited,
              elecTradeId: tradeToBeEdited.id,
              id: this.sortedHistoryItems[0]
                ? this.sortedHistoryItems[0].id + 1
                : 1,
            })
            await this.updateElecTradeMutator.mutateAsync(tradeData)
            await this.elecData.refetch()
          }
        } else {
          this.elecTradeMutator.mutateAsync(tradeData).then(() => {
            this.elecData.refetch()
          })
        }
      },
      handleConfirmClick(row) {
        this.selectedRowId = row.id
        this.isConfirmTradeDialogOpen = true
      },
      handleEditClick(row: ITradesElecData) {
        this.selectedRowId = row.id
        this.isEditForm = true
        this.isAddTradeDialogOpen = true

        this.tradeProductField = row.product as tradeProductOptions
        this.periodFirstField = this.getFirstFieldForPeriod(
          row.period as string,
        )
        this.periodSecondField = this.getSecondFieldForPeriod(
          row.period as string,
        )
        this.priceField = row.price
        this.buySellField = (row.volume as number) < 0 ? "Sell" : "Buy"
        this.volumeField = Math.abs(row.volume as number)
        this.traderField = row.trader as string
        this.notesField = row.notes
        this.percentOfBaseloadField = row.percentOfBaseload

        const [firstField, secondField] = this.getCounterpartyFields(
          row.counterParty as string,
        )
        this.counterPartyFirstField = firstField
        this.counterPartySecondField = secondField
      },
      handleUnlockClick(row: ITradesElecData) {
        this.unlockElecTradeMutator.mutateAsync({ id: row.id }).then(() => {
          this.elecData.refetch()
        })
      },
      confirmElecTrade(args: ITradesConfirmElecData) {
        if (!this.selectedRowId) return

        this.confirmElecTradeMutator
          .mutateAsync({
            id: this.selectedRowId,
            confirmReceivedDate: args.confirmReceivedDate,
            confirmSignedBy: args.confirmSignedBy,
            confirmSignedDate: args.confirmSignedDate,
          })
          .then(() => {
            this.elecData.refetch()
          })
      },
      clearTradeForm() {
        const form = this.$refs["add-elec-trade-form"] as typeof SoForm
        form?.reset()
        this.traderField = AuthService.getCurrentUser().name
      },
      resetAddTradeModal() {
        this.clearTradeForm()
        this.isEditForm = false
        this.selectedRowId = null
      },
      getPeriod() {
        return this.periodSecondField
          ? `${this.periodFirstField}${(
              this.periodSecondField as string
            ).substring(2)}`
          : this.periodFirstField
      },
      getFirstFieldForPeriod(period: string) {
        return period.slice(0, -2)
      },
      getSecondFieldForPeriod(period: string) {
        return tradePeriodSecondFieldOptions.find(
          (year) => year.title == (period as string).slice(-2),
        )?.value as string
      },
      onUpdateProductField(newValue: string) {
        if (newValue && !tradeProducts[newValue].allowsPercentOfBaseload) {
          this.percentOfBaseloadField = null
        }
      },
      getCounterpartyFields(counterParty: string) {
        return counterParty.split("-")
      },
      exportElecTrades() {
        const dataTable = this.$refs.dataTable as InstanceType<
          typeof SoDataTable
        >

        const worksheet = xlsx.utils.json_to_sheet(
          dataTable.dataTableItems.map((item) => {
            const { ...columnsToExport } = item
            return {
              ...columnsToExport,
              date: (item.date as Dayjs).toDate(),
              invoiceDateFrom: (item.invoiceDateFrom as Dayjs).toDate(),
              invoiceDateTo: (item.invoiceDateTo as Dayjs).toDate(),
              confirmReceivedDate: item.confirmReceivedDate
                ? (item.confirmReceivedDate as Dayjs).toDate()
                : "",
              confirmSignedDate: item.confirmSignedDate
                ? (item.confirmSignedDate as Dayjs).toDate()
                : "",
            }
          }),
          {
            header: this.headers.map((header) => header.key),
            dateNF: "d mmm yy",
          },
        )
        xlsx.utils.sheet_add_aoa(worksheet, [
          this.headers.map((header) => header.title),
        ])

        const workbook = xlsx.utils.book_new()
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1")
        xlsx.writeFile(
          workbook,
          `electricity_trades_${dayjs().format("DDMMYY")}.xlsx`,
        )
      },
    },
  })
</script>

<style scoped>
  .two-part-dropdown {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1rem;
  }

  .no-label-spacer {
    margin-top: 27px;
  }
  .so-data-table .v-data-table .v-table__wrapper > table tbody > tr > td {
    background-color: rgb(var(--v-theme-surface-dim));
  }
</style>
